import React, { useEffect, useState } from "react";
import { Card, CardBody, Row, Col, Button, Alert, Form } from "reactstrap";
import NumberFormat from "react-number-format";
import { saveAccount, updateAcctByCloseDate } from "../AccountAPI";
import { showApiError } from "../../utils/errorRoutingUtils";
import { saveFailAlert } from "../../utils/alertUtils";
import Swal from "sweetalert2";
import { useHistory } from "react-router-dom";
import shallow from "zustand/shallow";
import useCurrentAccountStore from "../AccountStore";
import {
	useDigitzsMerchantId,
	useFromUser,
} from "../../stores/LocalStorageHelper";
import dayjs from "dayjs";
import {
	CurrencyInput,
	SelectorElement,
} from "../../features/forms/components/InputElement";
import InputElement from "../../features/forms/components/InputElement";
import { useForm, FormProvider } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers";
import {
	paymentMethod,
	PaymentMode,
	PaymentType,
	PaymentTypeToModeMapping,
	PmtType,
} from "../../constants/Constants";
import InputWrapper from "../../features/forms/components/InputWrapper";
import ChildrenModal from "../../components/modals/ChildrenModal";
import DigitzsPayment from "../../services/digitzsService/DigitzsPayment";
import DigitzsAchPayment from "../../services/digitzsService/DigitzsAchPayment";
import AccountHook from "../AccountHook";

const LeaseEndSummary = ({ account, lease, leaseEndData }) => {
	const history = useHistory();
	const save = AccountHook();
	const [processing, setProcessing] = useState(false);
	const [notes, setNotes] = useState("");

	// Payment form states
	const today = dayjs().format("YYYY-MM-DD");
	const userID = useFromUser("ID");
	const dealerID = useFromUser("dealerID");
	const locationID = useFromUser("locationID");

	const [payment, setPayment] = useState({});
	const [datePaid, setDatePaid] = useState(today);
	const [amtPaid, setAmtPaid] = useState(account.payoff);
	const [method, setMethod] = useState("cash");
	const [receiptNo, setReceiptNo] = useState("");
	const [checkNo, setCheckNo] = useState("");
	const [description, setDescription] = useState("");
	const [note, setNote] = useState("");
	const [paymentNote, setPaymentNote] = useState("");
	const merchantId = useDigitzsMerchantId();
	const [digitzsModal, setDigitzsModal] = useState(false);
	const toggleDigitzsModal = () => {
		if (!digitzsModal) {
			const ID = getID();
			const newPayment = getNewPayment(ID);
			setPayment(newPayment);
			console.log(newPayment);
		}
		setDigitzsModal(!digitzsModal);
	};
	const remainingTax = account.tax - account.taxPaid;
	const adjustments =
		(account.debitCreditTotal || 0) - (account.debitPaid || 0);
	const pickupDue = (account.pickupTotal || 0) - (account.pickupPaid || 0);

	const { payments, editPayments } = useCurrentAccountStore(
		(state) => state,
		shallow
	);
	const [filteredPaymentMode, setFilteredPaymentMode] = useState([]);
	const [convenienceFee, setConvenienceFee] = useState(0);

	const schema = yup.object().shape({});
	const methods = useForm({
		reValidateMode: "onBlur",
		resolver: yupResolver(schema),
	});

	// Helper functions for payment processing
	const getOptionTitle = () => {
		switch (leaseEndData.selectedOption) {
			case "buyout":
				return "Vehicle Buyout";
			case "return":
				return "Lease Normal Completion - Return Vehicle";
			case "early_termination":
				return "Lease Early Termination";
			default:
				return "Lease End";
		}
	};

	const handleMethod = (e) => {
		setMethod(e);
	};

	const getID = () => {
		// For new payments, return null
		return null;
	};

	const getNewPayment = (ID) => ({
		...payment,
		accountRecNum: account.id,
		amtPaid: parseFloat(amtPaid),
		convenienceFee: parseFloat(convenienceFee),
		datePaid,
		receiptNo,
		method,
		checkNo,
		pmtType: "Lease",
		pmtMode: 100, // Regular payment
		status: "completed",
		note: paymentNote,
		description: `Lease End Payment - ${getOptionTitle()}`,
		dealerID,
		locationID,
		id: ID,
		userRecNum: userID,
		createOn: new Date(),
		appVersion: "2.0",
	});

	const addPayment = () => {
		const ID = getID();
		const newPayment = getNewPayment(ID);
		setPayment(newPayment);
		const updatedPayments = [...payments, newPayment];
		editPayments(updatedPayments);
	};

	// Calculate totals based on selected option
	const calculateTotals = () => {
		const { selectedOption, buyoutAmount, excessMileage, totalDamageFee } =
			leaseEndData;

		let totalDue = 0;
		let breakdown = [];

		if (selectedOption === "buyout") {
			// Buyout: Customer pays everything including residual
			totalDue = buyoutAmount + excessMileage.totalMileageFee + totalDamageFee;
			breakdown = [
				{ label: "Buyout Amount", amount: buyoutAmount },
				{
					label: "Excess Mileage Fees",
					amount: excessMileage.totalMileageFee,
				},
				{ label: "Damage Fees", amount: totalDamageFee },
			];
		} else {
			// Return or early termination: Only fees, no residual
			totalDue = excessMileage.totalMileageFee + totalDamageFee;
			breakdown = [
				{
					label: "Excess Mileage Fees",
					amount: excessMileage.totalMileageFee,
				},
				{ label: "Damage Fees", amount: totalDamageFee },
			];

			// Add remaining payments for early termination
			if (selectedOption === "early_termination") {
				const remainingPayments = account.amtFinanced - account.principle;
				totalDue += remainingPayments;
				breakdown.unshift({
					label: "Remaining Lease Payments",
					amount: remainingPayments,
				});
			}
		}

		return { totalDue, breakdown };
	};
	useEffect(() => {
		const modeValues = PaymentTypeToModeMapping["Lease"];
		const filteredModes = PaymentMode.filter((mode) =>
			modeValues.includes(mode.value)
		);
		setFilteredPaymentMode(filteredModes);
	}, []);

	const { totalDue, breakdown } = calculateTotals();

	// Save payment function
	const handleSavePayment = () => {
		if (!amtPaid || amtPaid <= 0) {
			Swal.fire({
				icon: "warning",
				title: "Amount Required",
				text: "Please enter a payment amount.",
			});
			return;
		}

		const newPayment = {
			accountRecNum: account.id,
			amtPaid: parseFloat(amtPaid),
			datePaid,
			receiptNo,
			method,
			checkNo,
			pmtType: "Lease",
			pmtMode: 100, // Regular payment
			status: "completed",
			note: paymentNote,
			description: `Lease End Payment - ${getOptionTitle()}`,
			dealerID,
			locationID,
			userRecNum: userID,
			createOn: new Date(),
			appVersion: "2.0",
		};

		const updatedPayments = [...payments, newPayment];
		editPayments(updatedPayments);

		Swal.fire({
			icon: "success",
			title: "Payment Saved",
			text: "Payment has been recorded successfully.",
			timer: 2000,
		});
	};

	const getSubStatus = () => {
		switch (leaseEndData.selectedOption) {
			case "buyout":
				return "PAID_OFF";
			case "return":
				return "LEASE_COMPLETED";
			case "early_termination":
				return "LEASE_EARLY_TERMINATION";
			default:
				return "LEASE_COMPLETED";
		}
	};

	const handleProcessLeaseEnd = async () => {
		// Validate payment amount if totalDue > 0
		if (totalDue > 0) {
			if (!amtPaid || amtPaid <= 0) {
				Swal.fire({
					icon: "warning",
					title: "Payment Required",
					text: "Please enter a payment amount before completing lease end.",
				});
				return;
			}

			// Check if payment amount matches total due (with some tolerance for convenience fees)
			const totalWithFee =
				parseFloat(amtPaid) + parseFloat(convenienceFee || 0);
			if (
				Math.abs(totalWithFee - totalDue) > 0.01 &&
				parseFloat(amtPaid) < totalDue
			) {
				const confirmResult = await Swal.fire({
					title: "Payment Amount Mismatch",
					html: `
						<p>Amount paid ($${parseFloat(amtPaid).toFixed(
							2
						)}) is less than total due ($${totalDue.toFixed(2)}).</p>
						<p>Do you want to continue with partial payment?</p>
					`,
					icon: "warning",
					showCancelButton: true,
					confirmButtonText: "Yes, Continue",
					cancelButtonText: "No, Go Back",
				});

				if (!confirmResult.isConfirmed) {
					return;
				}
			}
		}

		// Confirm action
		const confirmResult = await Swal.fire({
			title: `Complete ${getOptionTitle()}?`,
			html: `
				<p>Total amount: <strong>$${totalDue.toFixed(2)}</strong></p>
				<p>This action will close the lease account and cannot be undone.</p>
			`,
			showCancelButton: true,
			confirmButtonText: "Yes, Process Lease End",
			cancelButtonText: "Cancel",
			confirmButtonColor: "#28a745",
		});

		if (!confirmResult.isConfirmed) {
			return;
		}

		setProcessing(true);

		try {
			// Save payment if there's an amount due
			if (totalDue > 0 && amtPaid > 0) {
				const newPayment = {
					accountRecNum: account.id,
					amtPaid: parseFloat(amtPaid),
					convenienceFee: parseFloat(convenienceFee || 0),
					datePaid,
					receiptNo,
					method,
					checkNo,
					pmtType: "Lease",
					pmtMode: 100, // Regular payment
					status: "completed",
					note: note || paymentNote,
					description: description || `Lease End Payment - ${getOptionTitle()}`,
					dealerID,
					locationID,
					userRecNum: userID,
					createOn: new Date(),
					appVersion: "2.0",
				};

				const updatedPayments = [...payments, newPayment];
				editPayments(updatedPayments);

				// Save payment to database using AccountHook
				await save();
			}

			// Prepare account update
			// Parse existing notes if they're a JSON string
			let existingNotes = [];
			if (typeof account.notes === "string") {
				try {
					existingNotes = JSON.parse(account.notes);
				} catch (e) {
					existingNotes = [{ description: account.notes }];
				}
			} else if (Array.isArray(account.notes)) {
				existingNotes = account.notes;
			}

			// Add new lease end note
			const newNote = {
				description: `Lease End: ${getOptionTitle()}. Mileage Fees: $${leaseEndData.excessMileage.totalMileageFee.toFixed(
					2
				)}, Damage Fees: $${leaseEndData.totalDamageFee.toFixed(2)}. ${notes}`,
				date: new Date().toISOString(),
				user: "System",
			};

			// Combine notes and convert to JSON string for backend
			const updatedNotes = [...existingNotes, newNote];

			const updatedAccount = {
				...account,
				status: 1, // Closed
				subStatus: getSubStatus(),
				payoff: totalDue,
				closeDate: new Date().toISOString().split("T")[0],
				notes: JSON.stringify(updatedNotes), // Convert to JSON string for backend
			};

			// Save account changes
			await saveAccount({ account: updatedAccount });

			// Close account and handle inventory
			await updateAcctByCloseDate(account.id);

			Swal.fire({
				icon: "success",
				title: "Lease End Processed",
				text: `The lease has been successfully ${
					leaseEndData.selectedOption === "buyout" ? "bought out" : "closed"
				}.`,
			}).then(() => {
				// Reload the page to show updated account data
				window.location.reload();
			});
		} catch (error) {
			console.error("Error processing lease end:", error);
			showApiError(error, saveFailAlert);
		} finally {
			setProcessing(false);
		}
	};

	return (
		<div>
			<h4 className="mb-4">Summary & Final Payment</h4>
			<p className="text-muted mb-4">
				Review all details and process the final payment to complete the lease.
			</p>

			<Row>
				<Col md="6">
					<Card className="fee-summary-card">
						<CardBody>
							<h5 className="mb-3">Buyout</h5>

							<div className="fee-row">
								<span>Residual Value:</span>
								<strong>
									<NumberFormat
										value={lease.residualValue}
										displayType="text"
										thousandSeparator
										prefix="$"
										decimalScale={2}
										fixedDecimalScale
									/>
								</strong>
							</div>

							<div className="fee-row">
								<span>Remaining Principal:</span>
								<strong>
									<NumberFormat
										value={account.principleRemaining}
										displayType="text"
										thousandSeparator
										prefix="$"
										decimalScale={2}
										fixedDecimalScale
									/>
								</strong>
							</div>

							{account.interestRemaining > 0 && (
								<div className="fee-row">
									<span>Remaining Interest:</span>
									<strong>
										<NumberFormat
											value={account.interestRemaining}
											displayType="text"
											thousandSeparator
											prefix="$"
											decimalScale={2}
											fixedDecimalScale
										/>
									</strong>
								</div>
							)}

							{remainingTax > 0 && (
								<div className="fee-row">
									<span>Remaining Tax:</span>
									<strong>
										<NumberFormat
											value={remainingTax}
											displayType="text"
											thousandSeparator
											prefix="$"
											decimalScale={2}
											fixedDecimalScale
										/>
									</strong>
								</div>
							)}

							{account.lateFeeDue > 0 && (
								<div className="fee-row">
									<span>Late Fees:</span>
									<strong className="text-danger">
										<NumberFormat
											value={account.lateFeeDue}
											displayType="text"
											thousandSeparator
											prefix="$"
											decimalScale={2}
											fixedDecimalScale
										/>
									</strong>
								</div>
							)}

							{adjustments > 0 && (
								<div className="fee-row">
									<span>Adjustments Due:</span>
									<strong>
										<NumberFormat
											value={adjustments}
											displayType="text"
											thousandSeparator
											prefix="$"
											decimalScale={2}
											fixedDecimalScale
										/>
									</strong>
								</div>
							)}

							{pickupDue > 0 && (
								<div className="fee-row">
									<span>Pickup Payments Due:</span>
									<strong>
										<NumberFormat
											value={pickupDue}
											displayType="text"
											thousandSeparator
											prefix="$"
											decimalScale={2}
											fixedDecimalScale
										/>
									</strong>
								</div>
							)}

							<div className="fee-row">
								<strong className="text-success" style={{ fontSize: "1.3em" }}>
									<NumberFormat
										value={account.payoff}
										displayType="text"
										thousandSeparator
										prefix="$"
										decimalScale={2}
										fixedDecimalScale
									/>
								</strong>
							</div>
							{/* Mileage Summary */}
							{leaseEndData.excessMileage.totalMileageFee > 0 && (
								<Alert color="warning">
									<strong>Excess Mileage:</strong>{" "}
									{leaseEndData.excessMileage.excessMiles.toLocaleString()}{" "}
									miles × ${leaseEndData.excessMileage.ratePerMile.toFixed(2)}
									/mile
								</Alert>
							)}

							{/* Damages Summary */}
							{leaseEndData.damages.length > 0 && (
								<Alert color="warning">
									<strong>Documented Damages:</strong>{" "}
									{leaseEndData.damages.length} items totaling $
									{leaseEndData.totalDamageFee.toFixed(2)}
								</Alert>
							)}
							{totalDue === 0 && (
								<Alert color="info" className="mt-3 mb-0">
									No payment required. Click to complete the lease end process.
								</Alert>
							)}

							{totalDue > 0 && (
								<Alert color="warning" className="mt-3 mb-0">
									Please process payment above before completing lease end.
								</Alert>
							)}
						</CardBody>
					</Card>
				</Col>
				<Col md="6">
					<Card className="fee-summary-card">
						<CardBody>
							{/* Account Information */}
							<div className="mb-4">
								<h5>Account Information</h5>
								<div className="fee-row">
									<span>Account Number:</span>
									<strong>{account.accountNumber}</strong>
								</div>
								<div className="fee-row">
									<span>Customer:</span>
									<strong>{account.buyerName || "N/A"}</strong>
								</div>
								<div className="fee-row">
									<span>Vehicle:</span>
									<strong>
										{account.year} {account.make} {account.model}
									</strong>
								</div>
								<div className="fee-row">
									<span>VIN:</span>
									<strong>{account.vin || "N/A"}</strong>
								</div>
								<h6>What Happens Next?</h6>
								{leaseEndData.selectedOption === "buyout" && (
									<ul className="small mb-0">
										<li>Account will be marked as PAID_OFF</li>
										<li>Customer will own the vehicle</li>
										<li>Title will be processed for transfer</li>
									</ul>
								)}
								{leaseEndData.selectedOption === "return" && (
									<ul className="small mb-0">
										<li>Account will be closed as LEASE_COMPLETED</li>
										<li>Vehicle will return to inventory</li>
										<li>Deal status will be updated</li>
									</ul>
								)}
								{leaseEndData.selectedOption === "early_termination" && (
									<ul className="small mb-0">
										<li>Account will be closed as LEASE_EARLY_TERMINATION</li>
										<li>Vehicle will return to inventory</li>
										<li>Early termination fees applied</li>
									</ul>
								)}
							</div>
						</CardBody>
					</Card>
				</Col>
			</Row>
			<Row>
				<Col md="12">
					{/* Payment Form */}
					{totalDue > 0 && (
						<Card>
							<CardBody>
								<h5 className="mb-3">Process Payment</h5>

								<FormProvider className="" {...methods}>
									<Form>
										<InputWrapper
											inputComponents={[
												<InputElement
													{...methods}
													value={datePaid}
													onChange={(e) => {
														setDatePaid(e.target.value);
													}}
													name="datePaid"
													label="Date Paid"
													type="date"
												/>,
												<SelectorElement
													{...methods}
													readOnly={true}
													name="pmtType"
													label="Payment Type"
													value={"Lease"}
													options={PaymentType}
												/>,
												<SelectorElement
													{...methods}
													readOnly={true}
													name="pmtmode"
													label="Payment Mode"
													value={100}
													options={filteredPaymentMode}
												/>,
												<SelectorElement
													{...methods}
													locked={false}
													name="method"
													label="Methods"
													value={method}
													onChange={(e) => handleMethod(e.target.value)}
													options={paymentMethod}
												/>,

												<CurrencyInput
													disableValidation
													value={amtPaid}
													onChange={setAmtPaid}
													name="amountPaid"
													label="Amount Paid"
													type="number"
												/>,
												<CurrencyInput
													disableValidation
													value={convenienceFee}
													onChange={setConvenienceFee}
													name="convenienceFee"
													label="Convenience Fee"
													type="number"
												/>,
												<CurrencyInput
													disableValidation
													readOnly={true}
													value={amtPaid + convenienceFee}
													//onChange={setAmtPaid}
													name="totalPaid"
													label="Total Paid"
													type="number"
												/>,
												<InputElement
													{...methods}
													value={receiptNo}
													onChange={(e) => {
														setReceiptNo(e.target.value);
													}}
													name="receiptNo"
													label="Receipt #"
													type="text"
												/>,
												<InputElement
													{...methods}
													value={checkNo}
													onChange={(e) => {
														setCheckNo(e.target.value);
													}}
													name="checkNo"
													label="Reference No"
													type="text"
												/>,
												<div>
													{merchantId &&
														(method === "Credit Card" ||
															method === "Debit Card") && (
															<Button onClick={toggleDigitzsModal}>
																Process Credit Card
															</Button>
														)}
												</div>,
												<div>
													{merchantId && method === "ACH" && (
														<Button onClick={toggleDigitzsModal}>
															Process ACH
														</Button>
													)}
												</div>,
												<InputElement
													{...methods}
													value={description}
													onChange={(e) => {
														setDescription(e.target.value);
													}}
													colSize="col-sm-9"
													name="description"
													label="Description"
													type="text"
												/>,
												<InputElement
													{...methods}
													value={note}
													colSize="col-sm-12"
													onChange={(e) => {
														setNote(e.target.value);
													}}
													name="note"
													label="Note"
													type="textarea"
												/>,
											]}
										/>
									</Form>
								</FormProvider>
								<ChildrenModal
									size="lg"
									modal={digitzsModal}
									toggle={toggleDigitzsModal}
								>
									{method === "ACH" && (
										<DigitzsAchPayment
											pmtObj={payment}
											setPmtObj={setPayment}
											addPayment={addPayment}
											amtPaid={payment.amtPaid}
											accountID={account.id}
											loanPaid={payment.loanPaid}
											lateFeePaid={payment.lateFeePaid}
											adjustmentPaid={payment.adjustmentPaid}
											pickupPaid={null}
											convenienceFee={payment.convenienceFee}
										/>
									)}
									{(method === "Credit Card" || method === "Debit Card") && (
										<DigitzsPayment
											pmtObj={payment}
											setPmtObj={setPayment}
											addPayment={addPayment}
											amtPaid={payment.amtPaid}
											accountID={account.id}
											loanPaid={payment.loanPaid}
											lateFeePaid={payment.lateFeePaid}
											adjustmentPaid={payment.adjustmentPaid}
											pickupPaid={null}
											convenienceFee={payment.convenienceFee}
										/>
									)}
								</ChildrenModal>
							</CardBody>
						</Card>
					)}
				</Col>
			</Row>

			{/* Complete Lease End Button */}
			<Row className="mt-4">
				<Col className="text-center">
					<Button
						color="success"
						size="lg"
						onClick={handleProcessLeaseEnd}
						disabled={processing}
					>
						{processing ? "Processing..." : `Complete ${getOptionTitle()}`}
					</Button>
				</Col>
			</Row>
		</div>
	);
};

export default LeaseEndSummary;
